import { Link } from 'gatsby';
import {
  ArrowRight,
  Check,
  Eye,
  MessageCircle,
  Sparkles,
  Zap
} from 'lucide-react';
import React from 'react';
import { DemoButton } from '../components/DemoButton';
import { Headline } from '../components/Headline';
import { Reveal } from '../components/motion';
import { EarlyAccessBadge } from '../components/NewBadge';
import { ProductIcon } from '../components/ProductIcon';
import { SEO } from '../components/SEO';
import { DEMO_CAMPAIGNS, DEMO_MERCHANTS } from '../constants/demo-copy';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';

/*
 * Soft blob atmosphere (no horizon arc) — quieter than the homepage AI
 * section so feature mockups stay primary.
 */
const PageShell = styled('div')`
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    ${COLORS.WHITE} 0%,
    ${COLORS.GRAY.surfaceTint} 8%,
    ${COLORS.GRAY.surfaceTint} 92%,
    ${COLORS.WHITE} 100%
  );

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(
        ellipse 30% 42% at 12% 18%,
        rgba(120, 190, 255, 0.16) 0%,
        rgba(120, 190, 255, 0) 70%
      ),
      radial-gradient(
        ellipse 28% 40% at 88% 14%,
        rgba(160, 140, 255, 0.12) 0%,
        rgba(160, 140, 255, 0) 68%
      ),
      radial-gradient(
        ellipse 26% 38% at 48% 10%,
        rgba(190, 210, 255, 0.14) 0%,
        rgba(190, 210, 255, 0) 70%
      ),
      radial-gradient(
        ellipse 24% 36% at 72% 32%,
        rgba(90, 210, 230, 0.08) 0%,
        rgba(90, 210, 230, 0) 65%
      ),
      radial-gradient(
        ellipse 32% 38% at 10% 55%,
        rgba(46, 92, 255, 0.1) 0%,
        rgba(46, 92, 255, 0) 68%
      ),
      radial-gradient(
        ellipse 30% 36% at 90% 52%,
        rgba(120, 190, 255, 0.12) 0%,
        rgba(120, 190, 255, 0) 68%
      ),
      radial-gradient(
        ellipse 28% 34% at 40% 78%,
        rgba(160, 140, 255, 0.1) 0%,
        rgba(160, 140, 255, 0) 68%
      ),
      radial-gradient(
        ellipse 26% 32% at 66% 72%,
        rgba(90, 210, 230, 0.08) 0%,
        rgba(90, 210, 230, 0) 65%
      );
    mask-image: linear-gradient(
      180deg,
      transparent 0%,
      black 10%,
      black 88%,
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      180deg,
      transparent 0%,
      black 10%,
      black 88%,
      transparent 100%
    );
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const PageHero = styled('div')`
  text-align: center;
  max-width: 900px;
  margin: 140px auto 80px;
  padding: 0 24px;

  @media (max-width: 800px) {
    margin: 100px auto 60px;
  }
`;

const HeroBadgeRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const HeroDescription = styled('p')`
  font-size: 19px;
  line-height: 1.6;
  color: #4f4f4f;
  max-width: 740px;
  margin: 24px auto 36px;

  @media (max-width: 600px) {
    font-size: 15px;
    line-height: 1.55;
  }
`;

const FeatureSection = styled('section')<{ flip?: boolean }>`
  max-width: 1100px;
  margin: 0 auto;
  padding: 60px 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  direction: ${(p) => (p.flip ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
    direction: ltr;
  }
`;

const FeatureContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const FeatureBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
`;

const CheckList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 15px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const VisualPanel = styled('div')`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(14, 28, 43, 0.08);
  overflow: hidden;
`;

const ChatHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
`;

const ChatAvatar = styled('div')<{ $agent?: boolean }>`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: ${(p) => (p.$agent ? '8px' : '50%')};
  background: ${(p) => (p.$agent ? COLORS.PRIMARY : '#fff')};
  color: ${(p) => (p.$agent ? '#fff' : COLORS.PRIMARY)};
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${(p) => (p.$agent ? 'none' : '1px solid #e2e8f0')};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ChatHeaderName = styled('div')`
  font-size: 13px;
  font-weight: 700;
  color: #0e1c2b;
`;

const ChatHeaderStatus = styled('div')`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #52c41a;
    box-shadow: 0 0 6px rgba(82, 196, 26, 0.7);
  }
`;

const ChatBody = styled('div')`
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserBubbleRow = styled('div')`
  display: flex;
  justify-content: flex-end;
`;

const UserBubble = styled('div')`
  max-width: 92%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #0e1c2b;
  padding: 10px 14px;
  border-radius: 16px 16px 4px 16px;
  font-size: 14px;
  line-height: 1.5;
`;

const AgentReplyRow = styled('div')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const AgentBubble = styled('div')`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #4f4f4f;
`;

const PanelLabel = styled('div')`
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  padding: 24px 24px 12px;
  border-bottom: 1px solid #f1f5f9;
`;

const StatPanelBody = styled('div')`
  padding: 18px 24px 24px;
`;

const WhiteCard = styled('div')`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 12px;

  .title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
    margin-bottom: 6px;
  }

  .value {
    font-size: 15px;
    font-weight: 700;
    color: #0e1c2b;
  }

  .caption {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
  }
`;

const InlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 700px;
  margin: 80px auto 120px;
  padding: 0 24px;
`;

export default () => {
  return (
    <MainLayout>
      <PageShell>
        <SEO
          pathname="/ai-platform/"
          title="The Affilimate AI Platform for Commerce Publishers"
          description="An AI layer on the sponsorship and commerce platform premium publishers already run. Scope campaigns, catch delivery risk, and grow yield from the same commerce data."
        />

        <PageHero>
          <Reveal>
            <HeroBadgeRow>
              <Headline component="h1" style={{ margin: 0 }}>
                AI that acts on your sponsorship and commerce data
              </Headline>
            </HeroBadgeRow>
            <HeroBadgeRow>
              <EarlyAccessBadge />
            </HeroBadgeRow>
            <HeroDescription>
              Affilimate now runs on an AI-integrated foundation. Start using
              agentic tools that grow revenue in the places manual workflows
              couldn't reach, and put your data to work in ways that weren't
              possible before. Here's what that looks like today.
            </HeroDescription>
            <DemoButton text="Get a demo" variant="contained" />
          </Reveal>
        </PageHero>

        {/* Affilimate Agent */}
        <FeatureSection>
          <Reveal>
            <FeatureContent>
              <ProductIcon $size="lg">
                <MessageCircle size={22} />
              </ProductIcon>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Affilimate Agent
              </Headline>
              <FeatureBody>
                Your AI analyst inside Affilimate. It works on the same live
                data as your reports: transactions, campaigns, content,
                merchants, and product inventory. Answers come with structure,
                numbers, and links to the underlying views.
              </FeatureBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  Ask why revenue moved, which campaigns need attention, or
                  which inventory is worth pitching
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Context-aware on the surface you&apos;re in, from a campaign
                  overview to a specific article or report
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Answers link back to actual reports, so your team can verify
                  and go deeper
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  MCP access connects Claude, ChatGPT, and other AI tools your
                  team already uses
                </CheckItem>
              </CheckList>
              <InlineLink to="/mcp/">
                Learn about MCP access <ArrowRight size={18} />
              </InlineLink>
            </FeatureContent>
          </Reveal>
          <Reveal delay={150}>
            <VisualPanel aria-hidden>
              <ChatHeader>
                <ChatAvatar $agent>
                  <Sparkles size={14} />
                </ChatAvatar>
                <ChatHeaderName>Affilimate Agent</ChatHeaderName>
                <ChatHeaderStatus>Connected</ChatHeaderStatus>
              </ChatHeader>
              <ChatBody>
                <UserBubbleRow>
                  <UserBubble>
                    Which sponsorships are behind pace this quarter?
                  </UserBubble>
                </UserBubbleRow>
                <AgentReplyRow>
                  <ChatAvatar $agent>
                    <Sparkles size={14} />
                  </ChatAvatar>
                  <AgentBubble>
                    <WhiteCard style={{ marginTop: 0 }}>
                      <div className="title">Agent answer</div>
                      <div className="value">2 campaigns behind pace</div>
                      <div className="caption">
                        {DEMO_CAMPAIGNS.homeRefresh} at 38% of goal · Open
                        campaign report →
                      </div>
                    </WhiteCard>
                  </AgentBubble>
                </AgentReplyRow>
              </ChatBody>
            </VisualPanel>
          </Reveal>
        </FeatureSection>

        {/* Campaign infrastructure */}
        <FeatureSection>
          <Reveal>
            <FeatureContent>
              <ProductIcon $size="lg">
                <Sparkles size={22} />
              </ProductIcon>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Sell and prove sponsorship campaigns
              </Headline>
              <FeatureBody>
                Amplify is campaign infrastructure built over years at premium
                publishers: the same page, link, and commission data your team
                reconciles, extended into pitch, flight, delivery reporting, and
                renewal.
              </FeatureBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  Pitch and renew from measured performance history
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Flight and report without stitching spreadsheets
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Portfolio scale: many campaigns, one measured dataset
                </CheckItem>
              </CheckList>
              <InlineLink to="/product/amplify/">
                Explore Amplify campaigns <ArrowRight size={18} />
              </InlineLink>
            </FeatureContent>
          </Reveal>
          <Reveal delay={150}>
            <VisualPanel>
              <PanelLabel>Campaign portfolio</PanelLabel>
              <StatPanelBody>
                <WhiteCard style={{ marginTop: 0 }}>
                  <div className="title">In flight</div>
                  <div className="value">14 active campaigns</div>
                  <div className="caption">Across 6 brands · 3 portfolios</div>
                </WhiteCard>
                <WhiteCard>
                  <div className="title">Attributed revenue</div>
                  <div className="value">$2.4M · Q2 to date</div>
                  <div className="caption">
                    One place for reporting, finance, optimization, and
                    campaigns
                  </div>
                </WhiteCard>
              </StatPanelBody>
            </VisualPanel>
          </Reveal>
        </FeatureSection>

        {/* Agent analytics for commerce content */}
        <FeatureSection flip>
          <Reveal>
            <FeatureContent>
              <ProductIcon $size="lg">
                <Eye size={22} />
              </ProductIcon>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Agent analytics for commerce content
              </Headline>
              <FeatureBody>
                AI assistants already read and cite the commerce content you
                monetize. Agent analytics puts that activity next to pageviews,
                clicks, and commissions on the same page view, so you can see
                which pages agents visit most and how that attention is changing
                over time.
              </FeatureBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  AI crawls integrated next to pageviews, clicks, and
                  commissions
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Spot which monetized pages AI assistants visit most often
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Track how that activity changes month over month
                </CheckItem>
              </CheckList>
            </FeatureContent>
          </Reveal>
          <Reveal delay={150}>
            <VisualPanel>
              <PanelLabel>AI discovery · /best-espresso-machines</PanelLabel>
              <StatPanelBody>
                <WhiteCard style={{ marginTop: 0 }}>
                  <div className="title">AI crawls</div>
                  <div className="value">4,820 this month</div>
                  <div className="caption">+62% vs last month</div>
                </WhiteCard>
                <WhiteCard>
                  <div className="title">Page revenue</div>
                  <div className="value">$8,140 this month</div>
                  <div className="caption">
                    Same page, same view. AI activity and earnings together.
                  </div>
                </WhiteCard>
              </StatPanelBody>
            </VisualPanel>
          </Reveal>
        </FeatureSection>

        {/* Yield optimization */}
        <FeatureSection>
          <Reveal>
            <FeatureContent>
              <ProductIcon $size="lg">
                <Zap size={22} />
              </ProductIcon>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Yield optimization
              </Headline>
              <FeatureBody>
                Product-level swap opportunities surfaced from your own link
                inventory, with campaign suggestions waiting at the top of the
                page and scoped by AI to the content that's most likely to
                perform.
              </FeatureBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  Swap opportunities ranked by lift across your portfolio
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  AI suggestions surfaced for your team to review and activate
                  in minutes
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  You approve every swap. Optimization you control, not
                  outsource.
                </CheckItem>
              </CheckList>
              <InlineLink to="/product/amplify/">
                Explore Amplify campaigns <ArrowRight size={18} />
              </InlineLink>
            </FeatureContent>
          </Reveal>
          <Reveal delay={150}>
            <VisualPanel>
              <PanelLabel>Yield optimization</PanelLabel>
              <StatPanelBody>
                <WhiteCard style={{ marginTop: 0 }}>
                  <div className="title">Suggested campaign</div>
                  <div className="value">
                    {DEMO_MERCHANTS.retailPartner} swap · +$14.5K
                  </div>
                  <div className="caption">
                    11 pages scoped · Review draft →
                  </div>
                </WhiteCard>
                <WhiteCard>
                  <div className="title">Monthly opportunity volume</div>
                  <div className="value">+$77.9K</div>
                  <div className="caption">Across 23K monetizable clicks</div>
                </WhiteCard>
              </StatPanelBody>
            </VisualPanel>
          </Reveal>
        </FeatureSection>

        <FinalCta>
          <Reveal>
            <Headline component="h2">Learn more about the AI platform</Headline>
            <HeroDescription>
              These AI solutions are just the beginning. Get a demo and see how
              Affilimate can help your team grow revenue in new ways.
            </HeroDescription>
            <DemoButton text="Get a demo" variant="contained" />
          </Reveal>
        </FinalCta>
      </PageShell>
    </MainLayout>
  );
};
